import { createApp } from 'vue';
import App from './App.vue';
import VueGtag, { PageView } from 'vue-gtag';
import router from './router';
import store, { Commit } from './store';
import { Amplify, Auth, Hub } from 'aws-amplify';
import awsconfig from './aws-exports';
import { AuthMixin, ClassifyAuthFailure } from './utilities/AuthUtils';
import { DirectiveFunction } from '@/utilities/Apptable';
import { RouteLocation } from 'vue-router';
import Router from '@/router';
import VueFusionCharts from 'vue-fusioncharts';
import FusionCharts from 'fusioncharts';
import Column2D from 'fusioncharts/fusioncharts.charts';
import Widgets from 'fusioncharts/fusioncharts.widgets';
import FusionTheme from 'fusioncharts/themes/fusioncharts.theme.fusion';
import ExcelExport from 'fusioncharts/fusioncharts.excelexport';
import Powercharts from 'fusioncharts/fusioncharts.powercharts';
import AppTheme from '@/plugins/fusion';
import { authService } from '@/services/api';
import awsExports from '@/aws-exports';
import { StoreMutationType } from '@/types/StoreMutationType';
import { User } from '@/types/User';
import visible from './directives/visible';
import collapse from './directives/collapse';
import * as Sentry from '@sentry/vue';
import { useSessionExpiry } from '@/composables/useSessionExpiry';
import { ResolveLoginRedirect } from '@/utilities/LoginRedirect';

Amplify.configure({
  ...awsconfig,
  Config: {
    retryStrategy: {
      mode: 'standard',
      maxAttempts: 1
    }
  }
});
/*@ts-expect-error fusioncharts for vue doesn't have this member for now*/
FusionCharts.register('theme', AppTheme);

/*@ts-expect-error fusioncharts for vue doesn't have this member for now*/
FusionCharts.options.license({
  key: 'SiA8aA-16rA2E5C1C3I3B10B8C6C5D2F5F3E4qc1jA7A3okgC-7hC11A5cufF3F3I2B3B9B6C4C2C4C3H3I3D3C11A5C7qhhD4F1H3hD7E6F4A-9A-8kD2I3B6uwfB2C1C1uomB1E6B1C3F3C2A21A14B14A8D8bddH4C2WA9hlcE3E1A2raC5JD4E2F-11C-9hH1B3C2B4A4D4F3E4E2F2H3B3A4A5v==',
  creditLabel: false,
});

interface CustomPageView extends PageView {
  is_parent_account?: boolean;
}

async function checkIfUserPreviouslyLoggedIn(): Promise<void> {
  try {
    let currentUser: User | undefined = undefined;
    const userJson = localStorage.getItem('user');
    if (userJson) {
      currentUser = JSON.parse(userJson) as User;
    } else {

      await Auth.currentAuthenticatedUser();
      try {
        currentUser = await authService.getUserInfo();
      } catch(e) {
        console.error(e);
        Commit(StoreMutationType.SET_AUTH_FAILURE, ClassifyAuthFailure(e));
      }
    }
    if (currentUser) {
      Commit(StoreMutationType.LOGIN, currentUser);
    }
  } catch (e) {
    // Cognito's logout redirect lands here; sending it straight back to sign-in would
    // make signing out impossible.
    if (router.resolve(window.location.pathname).name === 'SignedOut') {
      return;
    }
    // amplify throws an error if the user is not logged in
    // this redirects to signin if not authenticated
    await useSessionExpiry().redirectToLogin();
    throw e; // throw instead of return so that the site doesn't render a blank page
  }
}

async function SetupAuthListener(): Promise<void> {
  Hub.listen('auth', ({ payload: { event, data } }) => {
    switch (event) {
      case 'signIn':
        break;
      case 'cognitoHostedUI':
        Auth.currentAuthenticatedUser().then(async () => {
          const userInfo = await authService.getUserInfo();

          // check if the user's tenant ID matches the app's expected tenant ID
          if (userInfo.clientId === awsExports.userPoolWebClientId) {
            Commit(StoreMutationType.LOGIN, userInfo);
          }

          // Amplify has already rewritten the URL to /auth, so the page the user asked for
          // survives only as the OAuth customState. Hub dispatches customOAuthState
          // synchronously after this event, so it is stored by the time this callback runs.
          const destination = ResolveLoginRedirect(Router, store.state.loginRedirectPath);
          Commit(StoreMutationType.SET_LOGIN_REDIRECT_PATH, undefined);
          return await Router.push(destination);
        }).catch(e => {
          console.log('auth error', e)
        });
        break;
      case 'signOut':
        Commit(StoreMutationType.LOGOUT);
        break;
      case 'tokenRefresh_failure':
        // Backstop. The probe in useSessionExpiry normally raises the modal before Amplify
        // ever attempts a refresh; if its assumptions about Amplify's storage layout or
        // clock handling ever break, this still catches the failure Amplify reports.
        useSessionExpiry().markSessionExpired();
        break;
      case 'customOAuthState':
        Commit(StoreMutationType.SET_LOGIN_REDIRECT_PATH, data);
        break;

    }
  });
  await checkIfUserPreviouslyLoggedIn();
}

SetupAuthListener()
  .then(() => {
    // render after attempting authentication
    const app = createApp(App);

    const [sentryDSN, sentryEnvironment, appVersion] = [
      import.meta.env.VITE_SENTRY_DSN,
      import.meta.env.VITE_SENTRY_ENVIRONMENT,
      `${__APP_VERSION__}`
    ];

    if (sentryDSN && sentryEnvironment) {
      Sentry.init({
        app,
        dsn: sentryDSN,
        environment: sentryEnvironment,
        release: appVersion,
        sendDefaultPii: true,
        integrations: [Sentry.captureConsoleIntegration({ levels: ['error', 'assert'] })],
        ignoreErrors: [
          'Signout timeout fail',
          // Raised locally in place of a request we already know would 401; the
          // SessionExpiredModal is the user-facing signal, not a Sentry issue.
          'Session has expired',
          'Network Error',
          'Request aborted',
          'timeout of',
          'ResizeObserver loop',
          'Non-Error promise rejection captured'
        ]
      });
    }


    app.use(store);
    app.use(router);
    app.use(VueFusionCharts, FusionCharts, Column2D, Widgets, FusionTheme, ExcelExport, Powercharts);
    app.use(
      VueGtag,
      {
        config: { id: import.meta.env.VITE_GA_MEASUREMENT_ID },
        bootstrap: false,
        pageTrackerTemplate(to?: RouteLocation): CustomPageView {
          return {
            page_title: to?.name?.toString() ?? undefined,
            page_path: to?.path,
            page_location: window.location.href,
            is_parent_account: to?.matched.some((record) => record.meta.isParentAccount),
          };
        },
      },
      router
    );
    app.directive('tablesort', DirectiveFunction);
    app.directive('visible', visible);
    app.directive('collapse', collapse);
    app.mixin(AuthMixin);
    app.mount('#app');

    // Expose manual testing helpers (dev only)
    if (import.meta.env.DEV) {
      const devHooks = window as unknown as {
        __flash: (text: string, color?: string) => void;
        __expireSession: (expired?: boolean) => void;
      };
      devHooks.__flash = (text, color) => {
        Commit(StoreMutationType.SET_FLASH_MESSAGE, { text, color });
      };
      devHooks.__expireSession = (expired = true) => {
        Commit(StoreMutationType.SET_SESSION_EXPIRED, expired);
      };
    }
  })
  .catch(console.error);
